/**
 * Hook to fetch transaction history from CollateralVault events
 * Fetches Deposit and Withdrawal events for a specific account
 * 
 * NOTE: Currently disabled due to RPC provider issues (eth.merkle.io)
 * Transaction history is non-critical and will be re-enabled when RPC is stable
 */

import { useEffect, useState } from 'react'
import { usePublicClient } from 'wagmi'
import { formatUnits, type Address } from 'viem'
import { getContractAddress } from '@contracts/addresses'
import { SUPPORTED_TOKENS } from '@contracts/tokens'
import type { Transaction } from '@components/vault/TransactionHistory'

// Transaction history now uses reliable public RPC endpoints with fallback support
// See src/config/rainbowkit.ts for RPC configuration
const ENABLE_TRANSACTION_HISTORY = true

/**
 * Fetch transaction history for an account
 */
export function useTransactionHistory(accountAddress?: Address) {
  const publicClient = usePublicClient()
  const [transactions, setTransactions] = useState<Transaction[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    // Early return if disabled
    if (!ENABLE_TRANSACTION_HISTORY) {
      setTransactions([])
      setIsLoading(false)
      setError(null)
      return
    }

    if (!accountAddress || !publicClient) {
      setTransactions([])
      return
    }

    const fetchHistory = async () => {
      try {
        setIsLoading(true)
        setError(null)

        const contractAddress = getContractAddress('CollateralVault')
        const currentBlock = await publicClient.getBlockNumber()
        
        // Fetch last 5,000 blocks (roughly 18 hours of history)
        // Reduced range to avoid RPC provider issues
        // In production, consider indexing or using a subgraph
        const fromBlock = currentBlock - 5000n

        // Fetch Deposit events (with error handling)
        let depositLogs: any[] = []
        try {
          depositLogs = await publicClient.getLogs({
            address: contractAddress,
            event: {
              type: 'event',
              name: 'Deposit',
              inputs: [
                { indexed: true, name: 'depositor', type: 'address' },
                { indexed: true, name: 'accountAddress', type: 'address' },
                { indexed: true, name: 'tokenAddress', type: 'address' },
                { indexed: false, name: 'amount', type: 'uint256' },
              ],
            },
            args: {
              accountAddress: accountAddress,
            },
            fromBlock,
            toBlock: 'latest',
          })
        } catch (err) {
          console.warn('⚠️ [Transaction History] Could not fetch deposits:', err instanceof Error ? err.message : err)
        }

        // Fetch Withdrawal events (with error handling)
        let withdrawalLogs: any[] = []
        try {
          withdrawalLogs = await publicClient.getLogs({
            address: contractAddress,
            event: {
              type: 'event',
              name: 'Withdrawal',
              inputs: [
                { indexed: true, name: 'accountAddress', type: 'address' },
                { indexed: true, name: 'tokenAddress', type: 'address' },
                { indexed: false, name: 'amount', type: 'uint256' },
                { indexed: true, name: 'destinationAddress', type: 'address' },
              ],
            },
            args: {
              accountAddress: accountAddress,
            },
            fromBlock,
            toBlock: 'latest',
          })
        } catch (err) {
          console.warn('⚠️ [Transaction History] Could not fetch withdrawals:', err instanceof Error ? err.message : err)
        }

        // Parse and combine events
        const allTransactions: Transaction[] = []

        // Parse deposits
        for (const log of depositLogs) {
          try {
            const block = await publicClient.getBlock({ blockNumber: log.blockNumber })
            const token = Object.values(SUPPORTED_TOKENS).find(
              (t) => t.address.toLowerCase() === log.args.tokenAddress?.toLowerCase()
            )
            
            if (token && log.args.amount) {
              allTransactions.push({
                id: log.transactionHash,
                type: 'deposit',
                asset: token.symbol,
                amount: formatUnits(log.args.amount, token.decimals),
                timestamp: new Date(Number(block.timestamp) * 1000),
                txHash: log.transactionHash,
              })
            }
          } catch (err) {
            // Skip this transaction if we can't fetch block details
            console.warn('⚠️ [Transaction History] Could not parse deposit:', err instanceof Error ? err.message : err)
          }
        }

        // Parse withdrawals
        for (const log of withdrawalLogs) {
          try {
            const block = await publicClient.getBlock({ blockNumber: log.blockNumber })
            const token = Object.values(SUPPORTED_TOKENS).find(
              (t) => t.address.toLowerCase() === log.args.tokenAddress?.toLowerCase()
            )
            
            if (token && log.args.amount) {
              allTransactions.push({
                id: log.transactionHash,
                type: 'withdrawal',
                asset: token.symbol,
                amount: formatUnits(log.args.amount, token.decimals),
                timestamp: new Date(Number(block.timestamp) * 1000),
                txHash: log.transactionHash,
              })
            }
          } catch (err) {
            // Skip this transaction if we can't fetch block details
            console.warn('⚠️ [Transaction History] Could not parse withdrawal:', err instanceof Error ? err.message : err)
          }
        }

        // Sort by timestamp (newest first)
        allTransactions.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime())

        setTransactions(allTransactions)
      } catch (err) {
        // RPC errors are common and non-critical - just return empty history
        console.warn('⚠️ [Transaction History] Could not fetch history (RPC issue):', err instanceof Error ? err.message : err)
        
        // Don't set error state - just silently return empty transactions
        // Transaction history is a nice-to-have feature, not critical
        setTransactions([])
        setError(null)
      } finally {
        setIsLoading(false)
      }
    }

    fetchHistory()
  }, [accountAddress, publicClient])

  return { transactions, isLoading, error }
}

